import { Collapse, IconButton } from "@mui/material";
import { useCardMargins } from "App/useCardMargins";
import {
  cardBorder,
  darkBlue,
  darkGrey,
  darkModeBackgroundHoveredColor,
  darkModeCardBackgroundColor,
  darkModeCardBorderColor,
  darkModeDarkTextColor,
  darkModeTextColor,
  medBlue,
} from "constants/colors";
import { CARD_BORDER_RADIUS_CSS, Z_INDEX } from "constants/constants";
import { ReactNode, useTransition } from "react";
import styled, {
  FlattenSimpleInterpolation,
  css,
} from "styled-components/macro";
import { useGetPageFromPath } from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useLocalStorageState } from "utils/useLocalStorageState";
import { CARD_TOPBAR_HEIGHT } from "./cardWithControlsUtils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useIsDarkMode } from "store/atoms";

type CardWithControlsProps = {
  children: ReactNode;
  title: string;
  isFullHeight?: boolean;
  shouldOverlayTitle?: boolean;
  shouldCenterTitle?: boolean;
  showTitleBorder?: boolean;
  showCardBorder?: boolean;
  customCss?: string | FlattenSimpleInterpolation;
  collapsible?: boolean;
  className?: string;
  CardConfigControls?: React.JSX.Element;
  onCollapse?: (nextExpanded: boolean) => void;
  id?: string;
  forceExpanded?: boolean;
  forceCollapsed?: boolean;
};
/** a component wrapper that adds a topbar including title, drag handle, and controls */
const CardWithControls = ({
  children,
  title,
  isFullHeight = true,
  shouldOverlayTitle = false,
  shouldCenterTitle = false,
  showTitleBorder = true,
  showCardBorder = true,
  customCss = "",
  collapsible = false,
  className = "",
  CardConfigControls,
  onCollapse,
  id = "",
  forceExpanded,
  forceCollapsed,
}: CardWithControlsProps) => {
  const { cardMarginInner, cardMarginOuter } = useCardMargins();
  const { isGridLayoutPage, pageName } = useGetPageFromPath();
  const { translate } = useFlightTranslation();

  const [_isExpanded, setIsExpanded] = useLocalStorageState(
    `collapsibles:${pageName}:${id ?? title}`,
    true
  );

  const isExpanded = forceCollapsed
    ? false
    : forceExpanded || !collapsible
      ? true
      : _isExpanded;
  const [isDarkMode] = useIsDarkMode();
  const [pending, startTransition] = useTransition();
  return (
    <CardStyles
      {...{
        className,
        $cardMarginInner: cardMarginInner,
        $cardMarginOuter: cardMarginOuter,
        $isFullHeight: isFullHeight,
        $shouldOverlayTitle: shouldOverlayTitle,
        $showCardBorder: showCardBorder,
        $isDarkMode: isDarkMode,
        $customCss: customCss,
      }}
    >
      <CardTopbarStyles
        className={`cardTopbar ${isExpanded ? "" : "collapsed"} ${pending ? "pending" : ""}`}
        data-testid={`card-topbar-${title.replace(/ /g, "-")}`}
        {...{
          $shouldOverlayTitle: shouldOverlayTitle,
          $shouldCenterTitle: shouldCenterTitle,
          $showTitleBorder: showTitleBorder && !shouldOverlayTitle,
          $isGridLayoutPage: isGridLayoutPage,
          $isExpanded: isExpanded,
          $collapsible: collapsible,
          $isDarkMode: isDarkMode,
        }}
        {...(collapsible
          ? {
              onClick: () => {
                const nextExpanded = !isExpanded;
                startTransition(() => {
                  setIsExpanded(nextExpanded);
                  onCollapse?.(nextExpanded);
                });
              },
            }
          : {})}
      >
        {collapsible && (
          <IconButton className="btnCollapse" size="large">
            <ExpandMoreIcon
              style={{
                transform: `rotate(${isExpanded ? 0 : -90}deg) translate(${
                  isExpanded ? "2px, -1px" : "-2px, 0px"
                })`,
              }}
            />
          </IconButton>
        )}
        <div className="cardTitle">{translate(title)}</div>
        {CardConfigControls ? (
          <div className="cardConfigControls">{CardConfigControls}</div>
        ) : null}
      </CardTopbarStyles>
      <Collapse in={!collapsible ? true : isExpanded} unmountOnExit={true}>
        <div className="cardContent">{children}</div>
      </Collapse>
    </CardStyles>
  );
};

export default CardWithControls;

const CardStyles = styled.div<{
  $cardMarginInner: number;
  $cardMarginOuter: number;
  $isFullHeight: boolean;
  $shouldOverlayTitle: boolean;
  $customCss: string | FlattenSimpleInterpolation;
  $showCardBorder: boolean;
  $isDarkMode: boolean;
}>`
  flex-shrink: 0;
  flex-grow: 0;
  position: relative;
  margin: ${props => props.$cardMarginInner}px;
  width: auto;
  height: ${props =>
    props.$isFullHeight
      ? css`calc(100% - ${props.$cardMarginInner * 2}px)`
      : "fit-content"};
  ${props =>
    props.$isFullHeight
      ? css`
          .MuiCollapse-container,
          .MuiCollapse-wrapper,
          .MuiCollapse-root:not(.MuiCollapse-hidden),
          .MuiCollapse-entered {
            height: 100%;
          }
        `
      : ""}
  box-sizing: border-box;
  position: relative;
  background: ${({ $isDarkMode }) =>
    $isDarkMode ? darkModeCardBackgroundColor : "white"};
  color: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : "black")};
  button {
    color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeDarkTextColor : darkGrey};
  }
  ${CARD_BORDER_RADIUS_CSS}
  border: ${({ $showCardBorder, $isDarkMode }) =>
    $showCardBorder
      ? css`1px solid ${$isDarkMode ? darkModeCardBorderColor : cardBorder}`
      : "none"};
  box-sizing: border-box;
  overflow: hidden;
  .cardContent {
    height: ${props =>
      props.$isFullHeight
        ? props.$shouldOverlayTitle
          ? "100%"
          : css`calc(100% - ${CARD_TOPBAR_HEIGHT}px)`
        : "fit-content"};
  }
  ${props => props.$customCss}
`;

const getCardTopbarHeight = (isExpanded: boolean) =>
  CARD_TOPBAR_HEIGHT + (isExpanded ? 0 : 6);

const CardTopbarStyles = styled.div<{
  $isDarkMode: boolean;
  $showTitleBorder: boolean;
  $shouldOverlayTitle: boolean;
  $shouldCenterTitle: boolean;
  $isGridLayoutPage: boolean;
  $isExpanded: boolean;
  $collapsible: boolean;
}>`
  &.pending {
    opacity: 0.5;
  }
  display: flex;
  justify-content: space-between;
  ${props =>
    props.$showTitleBorder
      ? css`
          border-bottom: 1px solid ${cardBorder};
          .btnOpenConfig {
            margin-top: -1px;
          }
        `
      : ""}
  height: ${props => getCardTopbarHeight(props.$isExpanded)}px;
  transition: all 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
  ${({ $isDarkMode, $collapsible }) =>
    $collapsible
      ? css`
          cursor: pointer;
          &:hover {
            background: ${$isDarkMode
              ? darkModeBackgroundHoveredColor
              : "hsla(0, 0%, 0%, 0.05)"};
          }
        `
      : ""}
  box-sizing: border-box;
  ${({ $shouldOverlayTitle, $isDarkMode, $isExpanded }) =>
    $shouldOverlayTitle
      ? css`
          position: absolute;
          top: ${getCardTopbarHeight($isExpanded)}px;
          margin-top: ${-getCardTopbarHeight($isExpanded)}px;
          left: 0;
          right: 0;
          z-index: ${Z_INDEX.L1};
          background: ${$isDarkMode
            ? darkModeBackgroundHoveredColor
            : "hsla(0, 0%, 100%, 0.4)"};
        `
      : ""}
  .cardTitle {
    padding: 5px 12px;
    color: ${({ $isDarkMode }) => ($isDarkMode ? medBlue : darkBlue)};
    font-size: ${props => (props.$isGridLayoutPage ? 12 : 14)}px;
    font-family: "GothamMedium";
    letter-spacing: 0.05em;
    ${props =>
      props.$shouldCenterTitle
        ? css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            width: fit-content;
            margin: 2px auto 0;
          `
        : ""}
  }
  position: relative;
  .cardConfigControls {
    width: fit-content;
    height: 22px;
    position: absolute;
    top: 0;
    right: 0;
    z-index: ${Z_INDEX.L10};
    display: flex;
    align-items: flex-start;
    transform: scale(0.75);
    transform-origin: top right;
  }
  .btnRangeSelectorConfig,
  .btnMoreMenu,
  .btnOpenConfig {
    .MuiSvgIcon-root {
      width: 22px;
      height: 22px;
    }
  }
  .btnOpenConfig {
    margin-top: 2px;
    margin-right: 4px;
  }
  .btnCollapse {
    width: 24px;
    height: 24px;
    .MuiSvgIcon-root {
      transition: transform 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
    }
  }
`;
